import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { SITE } from "@/config/site";
import { getPageSeo, type PageSeo } from "@/lib/api/seo.functions";
import { mergeSeo } from "@/lib/seo-head";

export const Route = createFileRoute("/about")({
  loader: () => getPageSeo({ data: { path: "/about" } }),
  head: ({ loaderData }) => mergeSeo({
    meta: [
      { name: "robots", content: "index, follow" },
      { title: "About Us · Surer Adda With Sanchayeeta — Live Singer & Music Platform" },
      { name: "description", content: "About Surer Adda With Sanchayeeta — a singer-focused platform by Sanchayeeta Seal for live performances, song covers, Bengali, Hindi, Bollywood, retro & devotional music." },
      { name: "keywords", content: "Surer Adda With Sanchayeeta, Sanchayeeta Seal, about Surer Adda, Bengali singer, live singer booking, song covers, Bollywood singer, retro music, devotional singer, event singer India" },
      { name: "author", content: "Sanchayeeta Seal" },
      { property: "og:title", content: "About Us · Surer Adda With Sanchayeeta" },
      { property: "og:description", content: "Surer Adda With Sanchayeeta — a musical platform by Sanchayeeta Seal celebrating live performance, soulful singing and authentic entertainment." },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Surer Adda With Sanchayeeta" },
      { property: "og:url", content: `${SITE.url}/about` },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/UlmYVFzrdxOqdg3qodJ5yNWZ9NW2/social-images/social-1780603530745-1000303195.webp" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "About Us · Surer Adda With Sanchayeeta" },
      { name: "twitter:description", content: "A singer-focused platform by Sanchayeeta Seal for live performances, song covers and soulful musical updates." },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/UlmYVFzrdxOqdg3qodJ5yNWZ9NW2/social-images/social-1780603530745-1000303195.webp" },
    ],
    links: [{ rel: "canonical", href: `${SITE.url}/about` }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "AboutPage",
        mainEntity: {
          "@type": "Person",
          name: "Sanchayeeta Seal",
          jobTitle: "Live Performance Artist, Versatile Singer, Voice Artist",
          description: "Founder of Surer Adda With Sanchayeeta — a musical platform for live performance, song covers and soulful entertainment.",
          url: "/about",
        },
      }),
    }],
  }, loaderData as PageSeo | null),
  component: AboutPage,
});

function AboutPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
        <p className="font-script text-4xl text-primary">About Us</p>
        <h1 className="mt-2 text-3xl font-semibold">About Sanchayeeta Seal — Live Performer &amp; Voice Artist</h1>

        <section className="prose prose-neutral mt-8 max-w-none text-foreground text-sm leading-relaxed">
          <p>
            Welcome to Surer Adda With Sanchayeeta — a musical platform created with love, passion,
            and dedication to celebrate the beauty of music, live performance, and soulful entertainment.
          </p>
          <p>
            Surer Adda With Sanchayeeta is a singer-focused website and digital platform where music
            lovers can explore live performances, song covers, musical updates, and artist-related
            content. Founded by Sanchayeeta, this platform is built to connect with audiences who enjoy
            melodious songs, stage performances, classic hits, Bollywood music, Bengali songs, retro
            favourites, devotional music, and modern live entertainment.
          </p>
          <p>
            Our mission is simple: to spread the joy of music and create a memorable musical experience
            for every listener.
          </p>

          <h2 className="mt-8 text-xl font-semibold">What We Offer</h2>
          <p>
            At Surer Adda With Sanchayeeta, we share a variety of music-related content and services,
            including:
          </p>
          <ul className="mt-3 list-disc space-y-1 pl-5">
            <li>Live singing performances</li>
            <li>Song covers and unplugged music videos</li>
            <li>Bengali, Hindi, Bollywood, retro and devotional songs</li>
            <li>Event booking and performance enquiries</li>
            <li>Music updates, artist posts, and audience engagement content</li>
            <li>Website and social media updates related to performances and events</li>
          </ul>
          <p>
            We believe music has the power to connect hearts, bring back memories, and make every
            occasion more special. Whether it is a stage show, cultural program, wedding event,
            private function, or festive celebration, our goal is to make every performance meaningful
            and enjoyable.
          </p>

          <h2 className="mt-8 text-xl font-semibold">Our Vision</h2>
          <p>
            Our vision is to build Surer Adda With Sanchayeeta as a trusted musical destination where
            audiences can discover quality performances, connect with an artist's journey, and enjoy
            authentic entertainment content in a safe and user-friendly online space.
          </p>
          <p>We are committed to:</p>
          <ul className="mt-3 list-disc space-y-1 pl-5">
            <li>Sharing original and engaging music-related content</li>
            <li>Maintaining a genuine connection with our audience</li>
            <li>Offering a smooth experience for visitors, listeners, and event organizers</li>
            <li>Promoting musical creativity with professionalism and passion</li>
          </ul>

          <h2 className="mt-8 text-xl font-semibold">Why Surer Adda With Sanchayeeta?</h2>
          <p>
            We understand that music is not just entertainment — it is emotion, memory, celebration,
            and expression. That is why every performance, every post, and every update on this website
            is created with care.
          </p>
          <p>Surer Adda With Sanchayeeta stands for:</p>
          <ul className="mt-3 list-disc space-y-1 pl-5">
            <li>Passion for singing and live performance</li>
            <li>Love for timeless melodies and audience connection</li>
            <li>A professional platform for music lovers and event organizers</li>
            <li>A growing online space dedicated to quality musical content</li>
          </ul>

          <h2 className="mt-8 text-xl font-semibold">Connect With Us</h2>
          <p>
            If you love music, enjoy live singing, or are looking to book a singer for your event,
            Surer Adda With Sanchayeeta is here to connect with you.
          </p>
          <p>
            Thank you for visiting our website and being part of this musical journey. Your support,
            love, and encouragement inspire us to continue sharing songs, performances, and moments
            that come straight from the heart.
          </p>
          <p>
            Let the music speak, let the melody connect, and let Surer Adda With Sanchayeeta bring
            rhythm to your celebrations.
          </p>
          <p className="mt-6 text-sm">
            Email{" "}
            <a className="text-primary hover:underline" href={`mailto:${SITE.contact.email}`}>{SITE.contact.email}</a>{" "}
            or call{" "}
            <a className="text-primary hover:underline" href={`tel:${SITE.contact.phone}`}>{SITE.contact.phone}</a>. You can also{" "}
            <Link to="/enquiry" className="text-primary hover:underline">send an enquiry</Link> or{" "}
            <Link to="/book" className="text-primary hover:underline">book a performance</Link> online.
          </p>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
